'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ChevronLeft, History, Plus, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { cn } from '@/shared/lib/cn';
import {
  getNoticeBannerActionClasses,
  LoadingSpinner,
  NoticeBanner,
} from '@/shared/ui';
import { CONTENT_COLUMN_INLINE_END_VAR } from '@/shared/ui/DesktopShell';
import { useBreakpoint, useMediaQuery } from '@/shared/hooks/use-media-query';
import { PulsingDots } from '@/shared/ui/PulsingDots';
import type {
  AssistantCommandBlock,
  AssistantHistoryPage,
  AssistantSessionSummary,
} from '@/types/assistant';
import { assistantService } from '../api/assistant.service';
import {
  scopeOf,
  TURN_MESSAGE_ID,
  useAssistantStore,
} from '../model/assistant.store';
import type { AssistantScreenContext } from '../hooks/use-assistant-screen';
import { useAssistantThread } from '../hooks/use-assistant-thread';
import { useAssistantActionRunner } from '../hooks/use-assistant-action-runner';
import { useAssistantCommands } from '../hooks/use-assistant-commands';
import { formatRelativeTime } from '../lib/relative-time';
import { useVoiceInput } from '../hooks/use-voice-input';
import { AssistantBlocks } from './AssistantBlocks';
import { AssistantComposer } from './AssistantComposer';

/**
 * Docked width by window: a laptop gives the content more room, an external
 * display lets the conversation breathe.
 */
function useAssistantPanelWidth() {
  const isWide = useMediaQuery('(min-width: 1440px)');
  const isUltra = useMediaQuery('(min-width: 1920px)');
  return isUltra ? 360 : isWide ? 320 : 288;
}

/**
 * The AI architect's panel (docs/assistant/ADR-001 §1): a bottom sheet over
 * 40% of a narrow window (a quarter while empty) with the design still visible above it, and a
 * 312 px column at the trailing edge of a wide one, the width of the settings
 * inspector. It never opens by itself; the launcher or a moment opens it.
 */
/** How long the fold-away animation runs; AssistantRoot keeps the panel mounted this long. */
export const ASSISTANT_PANEL_EXIT_MS = 240;

export function AssistantPanel({
  screen,
  closing = false,
}: {
  screen: AssistantScreenContext;
  /** The panel was closed and is folding into the launcher's corner. */
  closing?: boolean;
}) {
  const t = useTranslations('assistant');
  const tCommon = useTranslations('common');
  const close = useAssistantStore((s) => s.close);
  const turn = useAssistantStore((s) => s.turn);
  const queuedText = useAssistantStore((s) => s.queuedText);
  const scope = scopeOf(screen.buildingId);
  const panelWidth = useAssistantPanelWidth();
  // Phone island only: full height instead of 40%. The handle toggles it.
  const [expanded, setExpanded] = useState(false);
  const { isLg } = useBreakpoint();

  const {
    threadId,
    messages,
    startNewConversation,
    resumeSession,
    deleteHistory,
    isLoading,
    loadError,
    send,
    recordCardOutcome,
  } = useAssistantThread(screen, true);
  const [menuError, setMenuError] = useState<string | null>(null);
  // "Chat history" shown in place of the conversation; a new thread scope
  // (another building) starts on the chat.
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => setHistoryOpen(false), [threadId]);
  const runSessionAction = (action: () => Promise<void>) => {
    setMenuError(null);
    setHistoryOpen(false);
    action().catch((error: unknown) =>
      // The API's own words when it answered; the panel's for a lost connection.
      setMenuError(error instanceof ApiClientError && error.status >= 400 ? error.message : t('sendFailed'))
    );
  };
  const runAction = useAssistantActionRunner(screen.designId);
  const runCommand = useAssistantCommands();

  const appendTranscript = useCallback((text: string) => {
    const { draft, setDraft } = useAssistantStore.getState();
    setDraft(draft.trim() ? `${draft.trim()} ${text}` : text);
  }, []);
  const voice = useVoiceInput(threadId, appendTranscript);

  // Docked on a wide window, the panel takes 312 + 12 from the content column
  // (the shell narrows by the same amount); body-portaled overlays such as the
  // settings drill-down read this to stop at the panel instead of under it.
  useEffect(() => {
    if (closing) return;
    const root = document.documentElement;
    root.style.setProperty(CONTENT_COLUMN_INLINE_END_VAR, `${panelWidth + 12}px`);
    return () => {
      root.style.removeProperty(CONTENT_COLUMN_INLINE_END_VAR);
    };
  }, [panelWidth, closing]);

  // A moment's option tapped before the panel had its thread.
  useEffect(() => {
    if (!threadId || !queuedText) return;
    const text = useAssistantStore.getState().takeQueuedText();
    if (text) void send(text);
  }, [queuedText, send, threadId]);

  // A voice refusal carries the same blocks as a refused message; its paywall
  // opens like the stream's does.
  const voiceRefusal = voice.failure?.kind === 'api' ? voice.failure.blocks : null;
  const handledRefusal = useRef<unknown>(null);
  useEffect(() => {
    // Once per refusal, however often the panel re-renders with it.
    if (!voiceRefusal || handledRefusal.current === voiceRefusal) return;
    handledRefusal.current = voiceRefusal;
    const target = { designId: screen.designId };
    voiceRefusal
      .filter((block) => block.type === 'client_command')
      .forEach((block, index) =>
        runCommand(block as AssistantCommandBlock, {
          target,
          undoKey: `voice:${index}`,
        })
      );
  }, [voiceRefusal, runCommand, screen.designId]);

  const liveTurn = turn && turn.scope === scope && turn.threadId === threadId ? turn : null;
  const busy = !!turn || !threadId;
  const lastAssistantId = [...messages].reverse().find((m) => m.attributes.role === 'assistant')?.id;

  const scroller = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, liveTurn?.blocks.length, liveTurn?.status, liveTurn?.failed, voiceRefusal]);

  // The window's height changes under the feed (expand / collapse by the handle, the
  // history, the keyboard on a phone): without this the offset stays and the feed is left
  // somewhere in the middle of the conversation. Follow it to the newest message.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [historyOpen]);

  const sendText = (text: string) => {
    voice.clearFailure();
    void send(text);
  };

  // Re-read every time the history opens, so it has no cache to reconcile.
  const historyPage = useQuery({
    queryKey: cacheKeys.assistant.history(threadId ?? ''),
    queryFn: () => assistantService.listHistory(threadId!),
    enabled: historyOpen && !!threadId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  // Nothing to read yet: the narrow sheet hugs its content. In the history
  // that is no conversations or still loading.
  const conversationEmpty = historyOpen
    ? !historyPage.error && (historyPage.data?.sessions.length ?? 0) === 0
    : messages.length === 0 && !liveTurn && !menuError;

  return (
    <aside
      role="complementary"
      aria-label={t('title')}
      style={isLg ? { width: panelWidth } : undefined}
      className={cn(
        // Opaque: the conversation never mixes with the page behind it.
        'fixed z-[45] flex flex-col overflow-hidden border border-border/40 bg-background shadow-[0_12px_40px_-8px_rgba(0,0,0,0.28)]',
        closing ? 'animate-assistant-panel-out' : 'animate-assistant-panel-in',
        // One window that resizes between chat, history and empty states
        // instead of two windows swapping: `interpolate-size` lets `height`
        // animate to and from `auto` (browsers without it just snap).
        '[interpolate-size:allow-keywords] transition-[height,max-height] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
        // Narrow: a floating island, not a sheet: inset 12px on every side with
        // a full border and shadow, over the interface. The design stays visible
        // above it. With nothing to read yet it is only as tall as its content;
        // it closes with the header's button, not by dragging.
        'inset-x-3 bottom-[max(12px,env(safe-area-inset-bottom))] rounded-[20px]',
        expanded
          ? 'h-[calc(100dvh-max(12px,env(safe-area-inset-top))-max(12px,env(safe-area-inset-bottom)))] max-h-none'
          : conversationEmpty && !loadError
            ? 'h-auto max-h-[max(25dvh,300px)]'
            : 'h-[max(40dvh,320px)] max-h-[max(40dvh,320px)]',
        // Wide: the trailing column, as wide as the settings inspector.
        'lg:inset-x-auto lg:end-3 lg:top-3 lg:bottom-3 lg:h-auto lg:max-h-none lg:rounded-[16px]'
      )}
    >
      {/* Phone island only: a grab handle. Drag up expands to full height, drag
          down collapses (or closes from the compact size); a tap toggles. It is
          its own strip, so it never fights the conversation's scrolling. */}
      <PanelHandle
        expanded={expanded}
        onToggle={() => setExpanded((value) => !value)}
        onExpand={() => setExpanded(true)}
        onCollapse={() => (expanded ? setExpanded(false) : close())}
      />

      {/* Chat: title, New chat, Chat history, Close. History: Back, its title, Close. */}
      <header
        className={cn(
          'flex h-[56px] flex-shrink-0 items-center pe-[5px]',
          historyOpen ? 'ps-[5px]' : 'ps-4'
        )}
      >
        {/* The header element itself stays; only its content fades between the
            chat and the history states. */}
        <div
          key={historyOpen ? 'history' : 'chat'}
          className="animate-assistant-content-swap flex min-w-0 flex-1 items-center"
        >
        {historyOpen ? (
          <>
            <HeaderButton label={tCommon('back')} onClick={() => setHistoryOpen(false)}>
              <ChevronLeft className="size-[22px] rtl:-scale-x-100" />
            </HeaderButton>
            <h2 className="flex-1 truncate pe-2 text-section-title text-foreground">{t('history')}</h2>
          </>
        ) : (
          <>
            <h2 className="flex-1 truncate pe-2 text-section-title text-foreground">{t('title')}</h2>
            <HeaderButton
              label={t('newConversation')}
              disabled={busy}
              onClick={() => runSessionAction(startNewConversation)}
            >
              <Plus className="size-[22px]" />
            </HeaderButton>
            <HeaderButton
              label={t('history')}
              disabled={!threadId}
              onClick={() => {
                setMenuError(null);
                setHistoryOpen(true);
              }}
            >
              <History className="size-[22px]" />
            </HeaderButton>
          </>
        )}
        </div>
        <HeaderButton label={t('close')} onClick={close}>
          <X className="size-[22px]" />
        </HeaderButton>
      </header>

      <div
        key={historyOpen && threadId ? 'history' : 'chat'}
        className="animate-assistant-content-swap flex min-h-0 flex-auto flex-col"
      >
      {historyOpen && threadId ? (
        <AssistantHistory
          page={historyPage}
          busy={!!turn}
          onResume={(session) =>
            session.attributes.is_current
              ? setHistoryOpen(false)
              : runSessionAction(() => resumeSession(session.attributes.session))
          }
          onDeleteHistory={() => runSessionAction(deleteHistory)}
        />
      ) : (
        <>
      <div
        ref={scroller}
        className={cn('min-h-0 overflow-y-auto px-4 pb-3 space-y-4', conversationEmpty && !expanded ? 'flex-none lg:flex-1' : 'flex-1')}
      >
        {isLoading && (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="sm" />
          </div>
        )}
        {loadError && !isLoading && (
          <NoticeBanner variant="error" message={t('loadFailed')} />
        )}
        {menuError && <NoticeBanner variant="error" message={menuError} />}
        {!isLoading && !loadError && messages.length === 0 && !liveTurn && (
          <p className="py-6 text-center text-card-body text-muted-foreground">
            {screen.buildingId ? t('emptyBuilding') : t('emptyGeneral')}
          </p>
        )}

        {messages.map((message) =>
          message.attributes.role === 'user' ? (
            <UserBubble key={message.id} text={userText(message.attributes.blocks)} />
          ) : (
            <AssistantBlocks
              key={message.id}
              messageId={message.id}
              blocks={message.attributes.blocks}
              optionsEnabled={!busy && message.id === lastAssistantId}
              onOption={sendText}
              onAction={runAction}
              onCardOutcome={recordCardOutcome}
            />
          )
        )}

        {liveTurn && (
          <>
            <UserBubble text={liveTurn.text} />
            <AssistantBlocks
              messageId={TURN_MESSAGE_ID}
              blocks={liveTurn.blocks}
              optionsEnabled={false}
              onOption={sendText}
              onAction={runAction}
              onCardOutcome={recordCardOutcome}
            />
            {!liveTurn.failed && (liveTurn.status !== null || liveTurn.blocks.length === 0) && (
              <div className="flex items-center gap-2 text-card-meta text-muted-foreground">
                <PulsingDots size="sm" />
                <span>{liveTurn.status === 'looking_up' ? t('lookingUp') : t('thinking')}</span>
              </div>
            )}
            {liveTurn.failed && (
              <NoticeBanner
                variant="error"
                message={liveTurn.error ?? t('sendFailed')}
                action={
                  <button
                    type="button"
                    className={cn('font-medium', getNoticeBannerActionClasses('error'))}
                    onClick={() => {
                      const text = liveTurn.text;
                      useAssistantStore.getState().discardTurn();
                      void send(text);
                    }}
                  >
                    {t('retry')}
                  </button>
                }
              />
            )}
          </>
        )}

        {voiceRefusal && voiceRefusal.length > 0 && (
          <AssistantBlocks
            messageId="voice"
            blocks={voiceRefusal}
            optionsEnabled={false}
            onOption={sendText}
            onAction={runAction}
          />
        )}
        {voice.failure?.kind === 'api' && voice.failure.blocks.length === 0 && (
          <NoticeBanner variant="warning" message={voice.failure.message} />
        )}
        {voice.failure?.kind === 'permission' && (
          <NoticeBanner variant="warning" message={t('micDenied')} />
        )}
        {voice.failure?.kind === 'network' && (
          <NoticeBanner variant="error" message={t('sendFailed')} />
        )}
      </div>

      <div className="flex-shrink-0 px-3 pt-2 pb-3">
        <AssistantComposer disabled={busy} onSend={sendText} voice={voice} />
      </div>
        </>
      )}
      </div>
    </aside>
  );
}

/** Pull distance (px) that counts as a swipe rather than a tap. */
const HANDLE_SWIPE_PX = 24;

function PanelHandle({
  expanded,
  onToggle,
  onExpand,
  onCollapse,
}: {
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onCollapse: () => void;
}) {
  const t = useTranslations('assistant');
  const startY = useRef<number | null>(null);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={expanded ? t('collapse') : t('expand')}
      aria-expanded={expanded}
      onPointerDown={(event) => {
        startY.current = event.clientY;
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerUp={(event) => {
        if (startY.current === null) return;
        const delta = event.clientY - startY.current;
        startY.current = null;
        if (delta <= -HANDLE_SWIPE_PX) onExpand();
        else if (delta >= HANDLE_SWIPE_PX) onCollapse();
        else onToggle();
      }}
      onPointerCancel={() => {
        startY.current = null;
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      // 28px tall hit strip around a 36x4 pill; `touch-none` keeps the browser
      // from turning the drag into a page scroll.
      className="flex h-7 flex-shrink-0 cursor-grab touch-none items-end justify-center pb-1 lg:hidden"
    >
      <span className="h-1 w-9 rounded-full bg-foreground/20" />
    </div>
  );
}

/**
 * Header buttons: a bare 22 px glyph in the text colour on a 44 px tap target,
 * targets edge to edge; title `text-section-title` (the home page's block headers), header 56 tall (Apple `AssistantPanelView.headerButton`).
 */
function HeaderButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-11 flex-shrink-0 items-center justify-center text-foreground transition-opacity hover:opacity-70 disabled:opacity-50"
    >
      {children}
    </button>
  );
}

/**
 * "Chat history": the thread's conversations, newest used first, each a title
 * from the API and when it was last written in (Apple `AssistantHistoryView`).
 * Tapping a row reopens that conversation; "Delete history" sits under the list.
 */
function AssistantHistory({
  page,
  busy,
  onResume,
  onDeleteHistory,
}: {
  page: UseQueryResult<AssistantHistoryPage>;
  busy: boolean;
  onResume: (session: AssistantSessionSummary) => void;
  onDeleteHistory: () => void;
}) {
  const t = useTranslations('assistant');
  const locale = useLocale();
  const [confirming, setConfirming] = useState(false);

  if (page.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner size="sm" />
      </div>
    );
  }
  if (page.error || !page.data) {
    return (
      <div className="flex-1 px-4 py-3">
        <NoticeBanner variant="error" message={t('loadFailed')} />
      </div>
    );
  }
  const { sessions, deleteHistory: texts } = page.data;
  if (sessions.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-card-body text-muted-foreground">{t('historyEmpty')}</p>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {sessions.map((session) => (
        <button
          key={session.id}
          type="button"
          disabled={busy}
          aria-current={session.attributes.is_current || undefined}
          onClick={() => onResume(session)}
          className="flex w-full flex-col gap-0.5 px-4 py-2.5 text-start transition-colors hover:bg-muted/50 disabled:opacity-50"
        >
          <span className="w-full truncate text-card-body text-foreground">{session.attributes.title}</span>
          {session.attributes.last_message_at && (
            <span className="text-card-meta text-muted-foreground">
              {formatRelativeTime(session.attributes.last_message_at, locale)}
            </span>
          )}
        </button>
      ))}
      <button
        type="button"
        disabled={busy}
        onClick={() => setConfirming(true)}
        className="w-full p-4 text-start text-card-meta text-destructive disabled:opacity-50"
      >
        {t('deleteHistory')}
      </button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{texts.title}</AlertDialogTitle>
            <AlertDialogDescription>{texts.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{texts.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteHistory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {texts.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="ms-auto w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-[18px] bg-foreground/[0.08] px-3 py-2 text-composer-input text-foreground">
      {text}
    </div>
  );
}

function userText(blocks: Array<{ type: string }>): string {
  return blocks
    .map((block) => ('text' in block && typeof block.text === 'string' ? block.text : ''))
    .join('\n');
}
